<template>
  <ll-input
    v-model:value="innerValue"
    v-bind="attrs"
    :class="[bem.b()]"
    :disabled="props.disabled"
    :placeholder="props.placeholder"
    @blur="handleBlur"
  >
    <template #addonBefore>
      <ll-select
        v-model:value="innerPrefix"
        :options="httpUriOptions"
        style="min-width: 90px"
        :disabled="props.disabled"
        :allow-clear="false"
        @change="handleChange"
      />
    </template>
    <template v-if="props.suffix" #suffix>
      <span>{{ props.suffix }}</span>
    </template>
    <template v-if="props.addonAfter || showCopy" #addonAfter>
      <div :class="bem.e('addon-after')">
        <span v-if="props.addonAfter">{{ addonAfter }}</span>

        <ll-copy-text v-if="showCopy" :value="copyText">
          <template #value>
            <ll-icon icon-name="icon-copy" />
          </template>
        </ll-copy-text>
      </div>
    </template>
  </ll-input>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { Form } from 'ant-design-vue'
import type { Ref } from 'vue'
import { ref, useAttrs, watch } from 'vue'
import {
  getHttpMethodUri,
  httpUriOptions,
  uriInputEmits,
  uriInputProps,
} from './uri-input'

// 添加明确的类型定义
interface UriInputValue {
  prefix: string
  url: string
}

//Component Config
defineOptions({ name: 'LlUriInput' })
const props = defineProps(uriInputProps)
const emits = defineEmits(uriInputEmits)
const attrs = useAttrs()
const formItemContext = Form.useInjectFormItemContext()

const bem = createNamespace('uri-input')

const copyText = ref('')

// 为 ref 添加明确的类型注解
const innerValue: Ref<string> = ref<string>('')
const innerPrefix: Ref<string> = ref<string>('')

const handleChange = (): void => {
  const url: string = innerValue.value
    ? innerPrefix.value +
      innerValue.value +
      (props.suffix || '') +
      (props.addonAfter || '')
    : ''

  copyText.value = url ? url : innerPrefix.value
  emits('update:value', url)
  emits('change', url)
  formItemContext.onFieldChange()
}

const handleBlur = (e: Event): void => {
  if (attrs.onBlur && typeof attrs.onBlur === 'function') {
    attrs.onBlur(e)
  }
  formItemContext.onFieldBlur()
}

// 安全的值转换函数
const convertToString = (value: any): string => {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'string') {
    return value
  }

  if (value instanceof Date) {
    // 将 Date 对象转换为 ISO 字符串或其他格式
    return value.toISOString()
    // 或者使用其他格式，如：
    // return value.toLocaleDateString()
    // return value.getTime().toString()
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  // 对于对象或其他复杂类型
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

watch(
  () => props.value,
  (val: any): void => {
    // 安全地处理值转换
    const safeValue: string = convertToString(val)
    const { prefix, url }: UriInputValue = getHttpMethodUri(safeValue)

    let currentUrl: string = url
    if (url.includes(prefix)) {
      currentUrl = currentUrl.replace(prefix, '')
    }

    innerPrefix.value = prefix
    innerValue.value = currentUrl

    copyText.value = innerValue.value ? prefix + innerValue.value : prefix
    if (props.suffix) {
      innerValue.value = url.replace(props.suffix, '')
    }

    if (props.addonAfter) {
      innerValue.value = url.replace(props.addonAfter, '')
    }
  },
  {
    immediate: true,
  },
)

watch(
  innerValue,
  (): void => {
    handleChange()
  },
  {
    immediate: true,
  },
)

watch(
  () => props.addonAfter,
  () => {
    handleChange()
  },
)
</script>
